import { useState, useEffect } from 'react';
import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';
import { useCurrentUser } from '@/database/hooks/hooks';
import {
  FlywheelIcon,
  MattersIcon,
  TimeFeesIcon,
  ExpensesIcon,
  TasksIcon,
  InvoicesIcon,
  ResourcesIcon,
  WellnessIcon,
  OpsHrIcon,
  SettingsIcon,
} from '@/components/SidebarMenu/sidebarIcons';
import { UsersIcon } from 'lucide-react';
import type { SidebarMenuItem } from '@/components/SidebarMenu/SidebarMenu';
import { isTransactionStatusFailedOrReturned } from '@/utils/transactionStatus';

export const useSidebarMenuItems = () => {
  const authSession = useAuthenticatedSession();
  const { currentUser } = useCurrentUser();
  const isAdmin = !!currentUser?.roles?.some((r) =>
    ['admin', 'superAdmin'].includes(r),
  );
  const isSuperAdmin = !!currentUser?.roles?.includes('superAdmin');
  const [flywheelMenuItem, setFlywheelMenuItem] = useState<SidebarMenuItem>({
    label: 'Matters',
    path: '/matters',
    icon: <MattersIcon />,
  });
  const [tasksMenuItem, setTasksMenuItem] = useState<SidebarMenuItem>({
    label: 'Tasks',
    path: '/tasks',
    icon: <TasksIcon />,
  });
  const [financialsMenuItem, setFinancialsMenuItem] = useState<SidebarMenuItem>(
    {
      label: 'Financials',
      path: '/ops-hr',
      icon: <OpsHrIcon />,
    },
  );

  useEffect(() => {
    if (!authSession) {
      return;
    }
    const cancelMatters = authSession.db.subscribeToMattersChanges(
      (snapshot) => {
        const unviewed = snapshot.docs.filter((doc) => {
          return (
            !doc.data().viewedByResponsibleAttorney ||
            doc.data().viewedByResponsibleAttorney !==
              doc.data().responsibleAttorney
          );
        });
        setFlywheelMenuItem((prev) => ({
          ...prev,
          badgeCount: unviewed.length,
        }));
      },
      [
        ['responsibleAttorney', '==', authSession.userIds.firestore],
        ['status', '==', 'new'],
      ],
    );
    const cancelTasks = authSession.db.subscribeToTasksChanges(
      (snapshot) => {
        const unviewed = snapshot.docs.filter((doc) => {
          const data = doc.data();
          return (
            data.assignedTo === authSession.userIds.firestore &&
            data.viewedByAssignedTo !== data.assignedTo
          );
        });
        setTasksMenuItem((prev) => ({
          ...prev,
          badgeCount: unviewed.length,
        }));
      },
      [
        ['assignedTo', '==', authSession.userIds.firestore],
        ['isComplete', '!=', true],
      ],
    );

    let cancelAlertTransactions: (() => void) | undefined;
    let cancelDraftTransactions: (() => void) | undefined;

    if (isAdmin) {
      cancelAlertTransactions = authSession.db.subscribeToTransactionsChanges(
        [['status', 'in', ['failed', 'returned']]],
        (snapshot) => {
          if (!authSession?.userIds.firestore) return;
          const alertTransactions = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          const unviewedAlertTransactions = alertTransactions.filter(
            (t) =>
              isTransactionStatusFailedOrReturned(t.status) &&
              t.failedViewedBy?.includes(authSession.userIds.firestore) !==
                true,
          );
          setFinancialsMenuItem((prev) => {
            const failedCount = unviewedAlertTransactions.length;
            const draftCount = prev.draftCount ?? 0;
            const totalCount = failedCount + draftCount;
            return {
              ...prev,
              failedCount,
              badgeCount: totalCount,
              badgeColor: failedCount > 0 ? '#e03131' : undefined,
            };
          });
        },
      );
    }

    if (isSuperAdmin) {
      cancelDraftTransactions = authSession.db.subscribeToTransactionsChanges(
        [['status', '==', 'draft']],
        (snapshot) => {
          if (!authSession?.userIds.firestore) return;
          const drafts = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          const unviewedDrafts = drafts.filter(
            (t) =>
              t.draftViewedBy?.includes(authSession.userIds.firestore) !== true,
          );
          setFinancialsMenuItem((prev) => {
            const draftCount = unviewedDrafts.length;
            const failedCount = prev.failedCount ?? 0;
            const totalCount = failedCount + draftCount;
            return {
              ...prev,
              draftCount,
              badgeCount: totalCount,
              badgeColor: failedCount > 0 ? '#e03131' : undefined,
            };
          });
        },
      );
    }

    return () => {
      cancelTasks();
      cancelMatters();
      cancelAlertTransactions?.();
      cancelDraftTransactions?.();
    };
  }, [authSession, currentUser?.roles]);

  const menuItems: SidebarMenuItem[] = authSession
    ? [
        flywheelMenuItem,
        {
          label: 'Time/Fees',
          path: '/fees',
          icon: <TimeFeesIcon />,
        },
        {
          label: 'Expenses',
          path: '/expenses',
          icon: <ExpensesIcon />,
        },
        tasksMenuItem,
        {
          label: 'Contacts',
          path: '/contacts',
          icon: <UsersIcon />,
        },
        {
          label: 'Invoices',
          path: '/invoices',
          icon: <InvoicesIcon />,
        },
        {
          label: 'Resources',
          path: '/resources',
          icon: <ResourcesIcon />,
        },
        {
          label: 'Wellness',
          path: '/wellness',
          icon: <WellnessIcon />,
        },
        financialsMenuItem,
        ...(isAdmin
          ? [
              {
                label: 'Settings',
                path: '/settings',
                icon: <SettingsIcon />,
              },
            ]
          : []),
      ]
    : [];

  return menuItems;
};
